/**
 * Unit QR Code Card
 *
 * Displays a QR code for the unit's check-in token
 * Allows admin to print or download the QR code
 */

import { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { QrCode, Download, Printer, Copy, Check, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';

interface UnitQRCodeProps {
  unitCode: string;
  checkinToken: string | undefined;
  variant?: 'card' | 'button';
}

export function UnitQRCode({ unitCode, checkinToken, variant = 'card' }: UnitQRCodeProps) {
  const [copied, setCopied] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);

  if (!checkinToken) {
    if (variant === 'button') {
      return (
        <Button variant="outline" size="sm" disabled className="text-xs">
          <QrCode className="h-4 w-4 mr-2" />
          No Token
        </Button>
      );
    }
    return (
      <div className="rounded-lg border border-border p-4 text-center">
        <QrCode className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">QR code not available</p>
        <p className="text-xs text-muted-foreground mt-1">Token not generated</p>
      </div>
    );
  }

  // Generate the full check-in URL
  const checkInUrl = `${window.location.origin}/check-in/${checkinToken}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(checkInUrl);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleDownload = () => {
    // Get the SVG element
    const svgElement = qrRef.current?.querySelector('svg');
    if (!svgElement) {
      toast.error('QR code not found');
      return;
    }

    // Clone SVG and add white background
    const svgClone = svgElement.cloneNode(true) as SVGElement;
    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('width', '100%');
    rect.setAttribute('height', '100%');
    rect.setAttribute('fill', 'white');
    svgClone.insertBefore(rect, svgClone.firstChild);

    // Convert to data URL
    const svgData = new XMLSerializer().serializeToString(svgClone);
    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    // Create download link
    const link = document.createElement('a');
    link.href = url;
    link.download = `${unitCode}-checkin-qr.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('QR code downloaded');
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to print');
      return;
    }

    // Get SVG content
    const svgElement = qrRef.current?.querySelector('svg');
    if (!svgElement) {
      toast.error('QR code not found');
      printWindow.close();
      return;
    }

    const svgData = new XMLSerializer().serializeToString(svgElement);
    // Sanitize the SVG data before inserting into DOM
    const sanitizedSvg = DOMPurify.sanitize(svgData, { USE_PROFILES: { svg: true } });

    // Build the print page using DOM methods
    const doc = printWindow.document;

    // Create style element
    const style = doc.createElement('style');
    style.textContent = `
      body {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        margin: 0;
        font-family: system-ui, sans-serif;
      }
      .container {
        text-align: center;
        padding: 40px;
      }
      h1 {
        font-size: 24px;
        margin-bottom: 20px;
      }
      .qr-code {
        margin: 20px auto;
      }
      .url {
        font-size: 12px;
        color: #666;
        word-break: break-all;
        max-width: 300px;
        margin-top: 20px;
      }
      @media print {
        body { margin: 0; }
      }
    `;
    doc.head.appendChild(style);

    // Set title
    doc.title = `QR Code - ${unitCode}`;

    // Create container
    const container = doc.createElement('div');
    container.className = 'container';

    // Create heading
    const heading = doc.createElement('h1');
    heading.textContent = `Check In/Out: ${unitCode}`;
    container.appendChild(heading);

    // Create QR code container using DOMPurify for safe SVG insertion
    const qrContainer = doc.createElement('div');
    qrContainer.className = 'qr-code';
    // Parse the sanitized SVG and append as DOM node
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(sanitizedSvg, 'image/svg+xml');
    const svgNode = doc.importNode(svgDoc.documentElement, true);
    qrContainer.appendChild(svgNode);
    container.appendChild(qrContainer);

    // Create URL text
    const urlText = doc.createElement('p');
    urlText.className = 'url';
    urlText.textContent = checkInUrl;
    container.appendChild(urlText);

    doc.body.appendChild(container);

    // Print after a short delay to ensure rendering
    setTimeout(() => {
      printWindow.print();
      printWindow.onafterprint = () => {
        printWindow.close();
      };
    }, 100);
  };

  const handleOpenCheckIn = () => {
    window.open(checkInUrl, '_blank');
  };

  const qrCodeContent = (
    <div ref={qrRef} className="flex flex-col items-center">
      <div className="bg-white p-3 rounded-lg">
        <QRCodeSVG
          value={checkInUrl}
          size={180}
          level="H"
          includeMargin={false}
        />
      </div>
      <p className="text-xs text-muted-foreground mt-3 text-center break-all max-w-[200px]">
        {checkInUrl}
      </p>
    </div>
  );

  const actionButtons = (
    <div className="flex flex-wrap gap-2 justify-center mt-4">
      <Button
        variant="outline"
        size="sm"
        onClick={handleCopyLink}
        className="text-xs"
      >
        {copied ? (
          <Check className="h-3 w-3 mr-1" />
        ) : (
          <Copy className="h-3 w-3 mr-1" />
        )}
        Copy Link
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={handleDownload}
        className="text-xs"
      >
        <Download className="h-3 w-3 mr-1" />
        Download
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={handlePrint}
        className="text-xs"
      >
        <Printer className="h-3 w-3 mr-1" />
        Print
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={handleOpenCheckIn}
        className="text-xs"
      >
        <ExternalLink className="h-3 w-3 mr-1" />
        Test
      </Button>
    </div>
  );

  if (variant === 'button') {
    return (
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="text-xs">
            <QrCode className="h-4 w-4 mr-2" />
            QR Code
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">Check-In QR Code</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {qrCodeContent}
            {actionButtons}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Card variant
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <QrCode className="h-4 w-4 text-[#8FB560]" />
          <span className="font-medium text-sm">Check-In QR Code</span>
        </div>
      </div>
      <div className="p-4">
        {qrCodeContent}
        {actionButtons}
      </div>
    </div>
  );
}
